import React from "react";
import { useTranslation } from "context/TranslationProvider";

const fmtTimestamp = (ts, t) => {
  if (!ts) return "";
  const dateObj = new Date(ts);
  const dateStr = dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const timeStr = dateObj.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  return `${dateStr} | ${timeStr}`;
};

const buildTimelineSteps = (ticketData, t) => {
  const history = (ticketData && Array.isArray(ticketData.statusHistory)) ? ticketData.statusHistory : [];
  const getAt = (status) => history.find((h) => h.status === status)?.changedAt || null;

  const currentStatus = ticketData?.status || "pending";
  const isAssigned = !!ticketData?.assignedTo;
  const assignedTitle = isAssigned
    ? t("page.support-tickets.timeline.assignedTitle", "Assigned to Moderator")
    : t("page.support-tickets.timeline.unassignedTitle", "Waiting for Moderator");

  const assignedDescription = isAssigned
    ? t("page.support-tickets.timeline.assignedDescriptionAssigned", "Your ticket has been assigned to a moderator and will be reviewed shortly.")
    : t("page.support-tickets.timeline.assignedDescriptionPending", "Waiting for a moderator to be assigned.");

  const isResolved = currentStatus === "resolved";
  const isInProgress = currentStatus === "in_progress";

  const steps = [
    {
      key: "created",
      label: t("page.support-tickets.timeline.createdTitle", "Ticket created"),
      description: t("page.support-tickets.timeline.createdDescription", "Your ticket has been received and is waiting to be reviewed."),
      icon: "clock",
      timestamp: ticketData?.createdAt,
      isComplete: true,
      isCurrent: false,
    },
    {
      key: "assigned",
      label: assignedTitle,
      description: assignedDescription,
      icon: "person",
      timestamp: ticketData?.assignedAt || null,
      isComplete: isAssigned,
      isCurrent: !isAssigned && currentStatus === "pending",
    },
    {
      key: "in_progress",
      label: t("page.support-tickets.timeline.inProgressTitle", "In progress"),
      description: t("page.support-tickets.timeline.inProgressDescription", "The moderator is reviewing your issue."),
      icon: "search",
      timestamp: getAt("in_progress"),
      isComplete: isResolved,
      isCurrent: isInProgress || (isAssigned && currentStatus === "pending"),
    },
    {
      key: "resolved",
      label: t("page.support-tickets.timeline.resolvedTitle", "Resolved"),
      description: t("page.support-tickets.timeline.resolvedDescription", "Your issue has been resolved."),
      icon: "check",
      timestamp: ticketData?.resolvedAt || getAt("resolved"),
      isComplete: isResolved,
      isCurrent: isResolved,
    },
  ];

  return steps;
};

const TicketTimeline = ({ ticketData }) => {
  const { t } = useTranslation();
  const steps = buildTimelineSteps(ticketData, t);

  return (
    <div className="tracker-timeline-container mt-4">
      {steps.map((step, idx) => {
        const isLast = idx === steps.length - 1;
        const isComplete = step.isComplete;
        const isCurrent = step.isCurrent;
        return (
          <div key={step.key} className="tracker-timeline-step">
            <div className="tracker-timeline-icon-wrapper">
              <div className={`tracker-timeline-icon-circle ${isComplete ? "complete" : ""} ${isCurrent ? "current" : ""} ${step.icon}`}>
                {step.icon === "clock" && <i className="ni ni-time-alarm" />}
                {step.icon === "person" && <i className="ni ni-single-02" />}
                {step.icon === "search" && <i className="ni ni-zoom-split-in" />}
                {step.icon === "check" && <i className="ni ni-check-bold" />}
              </div>
              {!isLast && <div className={`tracker-timeline-line ${isComplete ? "complete" : ""}`} />}
            </div>
            <div className={`tracker-timeline-content ${!isComplete && !isCurrent ? "centered" : ""}`}>
              <div>
                <p className={`tracker-timeline-step-title ${!isComplete && !isCurrent ? "incomplete" : ""}`}>
                  {step.label}
                </p>
                {(isComplete || isCurrent) && (
                  <p className="tracker-timeline-step-desc">
                    {step.description}
                  </p>
                )}
              </div>
              {step.timestamp && (
                <div className="tracker-timeline-timestamp">
                  {fmtTimestamp(step.timestamp, t)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TicketTimeline;
